import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ScanLine } from 'lucide-react';
import apiClient from '../../api/client';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../components/ui/Toast';
import { formatDate, formatEUR } from '../../lib/format';
import { cn } from '../../utils/cn';
import {
  cmdHeaders,
  returnError,
  Field,
  Panel,
  PortalReturnTabs,
  tdCls,
  thCls,
  useReturnsRole,
  type Policy,
  type ReturnRow,
} from '../../components/returns/returnsShared';
import { ReturnsTable } from './ReturnsList';

/**
 * The returns work queues (returns v2): Reception — a part comes in only under a valid return
 * number; Validations — what the rules leave to a manager; Supplier returns — parts going back,
 * grouped by supplier and type, the supplier's answer line by line; Rules — the policy.
 */

const useBoard = (group: string) => {
  const [rows, setRows] = useState<ReturnRow[] | null>(null);
  const { showApiError } = useToast();
  const load = useCallback(() => {
    apiClient
      .get<ReturnRow[]>(`/returns/board?group=${group}`)
      .then((r) => setRows(r.data))
      .catch((e) => {
        setRows([]);
        showApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);
  useEffect(load, [load]);
  return { rows, load };
};

/**
 * A block of a work queue: a card on its own page, a plain section when the queue is a tab of
 * the Returns page (the tab's card already frames it).
 */
function Block({
  embedded,
  title,
  actions,
  children,
  className,
}: {
  embedded: boolean;
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  if (!embedded)
    return (
      <Panel className={className} title={title} actions={actions}>
        {children}
      </Panel>
    );
  return (
    <section className="border-t">
      <div className="flex flex-wrap items-center gap-2 px-4 pb-1 pt-3">
        <h2 className="flex-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** Reception — a tab of the Returns page (`embedded`), or its own page at the old address. */
export function ReturnsReception({
  embedded = false,
  onChange,
}: { embedded?: boolean; onChange?: () => void } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { push } = useToast();
  const { can } = useReturnsRole();
  const { rows, load } = useBoard('TODO');
  const [num, setNum] = useState('');
  const [err, setErr] = useState('');
  const receive = async (number: string, id?: string) => {
    setErr('');
    try {
      const { data } = await apiClient.post<{ id: string; returnNumber: string }>(
        '/returns/receive',
        { number },
        cmdHeaders()
      );
      push({
        tone: 'success',
        message: t('returnsV2.toast.received', { number: data.returnNumber }),
      });
      navigate(`/returns/${data.id ?? id}`);
    } catch (e) {
      setErr(returnError(t, e));
      load();
      onChange?.();
    }
  };
  const expected = (rows ?? []).filter((r) => r.status === 'APPROVED');
  const toControl = (rows ?? []).filter((r) => r.status === 'RECEIVED');
  return (
    <div className="app-tokens">
      {!embedded && (
        <PageHeader
          title={t('returnsV2.reception.title')}
          description={t('returnsV2.reception.description')}
        />
      )}
      <div className={embedded ? 'px-4 py-3' : 'mb-3 rounded-lg border bg-card p-3 shadow-sm'}>
        {embedded && (
          <p className="mb-2 text-xs text-muted-foreground">
            {t('returnsV2.reception.description')}
          </p>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <Field label={t('returnsV2.reception.number')} className="min-w-[16rem] flex-1">
            <div className="relative">
              <ScanLine className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8 font-mono uppercase"
                value={num}
                onChange={(e) => setNum(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && num.trim()) void receive(num.trim());
                }}
                placeholder="RET-2026-00042"
              />
            </div>
          </Field>
          <Button
            disabled={!can('receive') || !num.trim()}
            onClick={() => void receive(num.trim())}
          >
            {t('returnsV2.action.receive')}
          </Button>
        </div>
        {err && (
          <p className="mt-2 text-sm font-medium text-destructive" role="alert">
            {err}
          </p>
        )}
      </div>
      <Block
        embedded={embedded}
        className="mb-3"
        title={t('returnsV2.reception.expected')}
        actions={
          <span className="text-xs text-muted-foreground">
            {t('returnsV2.reception.byDriver', {
              n: expected.filter((r) => r.pickup === 'DRIVER').length,
            })}
          </span>
        }
      >
        {rows === null ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            {t('returnsV2.loading')}
          </p>
        ) : (
          <ReturnsTable
            rows={expected}
            action={(r) =>
              can('receive') ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void receive(r.returnNumber, r.id)}
                >
                  {t('returnsV2.action.receive')}
                </Button>
              ) : null
            }
          />
        )}
      </Block>
      <Block embedded={embedded} title={t('returnsV2.reception.toControl')}>
        {rows === null ? null : (
          <ReturnsTable
            rows={toControl}
            action={(r) => (
              <Button size="sm" onClick={() => navigate(`/returns/${r.id}`)}>
                {t('returnsV2.action.control')}
              </Button>
            )}
          />
        )}
      </Block>
    </div>
  );
}

/** Validations — a tab of the Returns page (`embedded`), or its own page at the old address. */
export function ReturnsValidations({
  embedded = false,
  onChange,
}: { embedded?: boolean; onChange?: () => void } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { push, showApiError } = useToast();
  const { can } = useReturnsRole();
  const { rows, load } = useBoard('TODO');
  const [policy, setPolicy] = useState<Policy | null>(null);
  useEffect(() => {
    apiClient
      .get<{ policy: Policy }>('/returns/policy')
      .then((r) => setPolicy(r.data.policy))
      .catch(() => undefined);
  }, []);
  const toDecide = (rows ?? []).filter((r) => r.status === 'REQUESTED');
  const cash = (rows ?? []).filter(
    (r) => r.status === 'INSPECTED' && r.cashApprovalRequested && !r.cashApproved
  );
  return (
    <div className="app-tokens">
      {embedded ? (
        <p className="px-4 pt-3 text-xs text-muted-foreground">
          {t('returnsV2.validations.description', {
            amount: formatEUR(policy?.cashApprovalAbove ?? 150),
          })}
        </p>
      ) : (
        <PageHeader
          title={t('returnsV2.validations.title')}
          description={t('returnsV2.validations.description', {
            amount: formatEUR(policy?.cashApprovalAbove ?? 150),
          })}
        />
      )}
      {!can('decide') && (
        <div
          className={cn(
            'rounded-md bg-primary-soft px-3 py-2 text-sm text-primary',
            embedded ? 'mx-4 my-2' : 'mb-3'
          )}
        >
          {t('returnsV2.validations.readOnly')}
        </div>
      )}
      <Block embedded={embedded} className="mb-3" title={t('returnsV2.validations.toDecide')}>
        {rows === null ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            {t('returnsV2.loading')}
          </p>
        ) : (
          <ReturnsTable
            rows={toDecide}
            action={(r) =>
              can('decide') ? (
                <Button size="sm" onClick={() => navigate(`/returns/${r.id}`)}>
                  {t('returnsV2.action.decide')}
                </Button>
              ) : null
            }
          />
        )}
      </Block>
      <Block embedded={embedded} title={t('returnsV2.validations.cash')}>
        {rows === null ? null : (
          <ReturnsTable
            rows={cash}
            action={(r) =>
              can('manager') ? (
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      await apiClient.post(`/returns/${r.id}/cash-approve`, {}, cmdHeaders());
                      push({ tone: 'success', message: t('returnsV2.toast.cashApproved') });
                      load();
                      onChange?.();
                    } catch (e) {
                      showApiError(e);
                    }
                  }}
                >
                  {t('returnsV2.settle.approveCash')}
                </Button>
              ) : null
            }
          />
        )}
      </Block>
    </div>
  );
}

interface BrfRow {
  id: string;
  brfNumber: string;
  status: string;
  supplierName: string;
  returnType: string | null;
  createdAt: string;
  shippedAt: string | null;
  lineCount: number;
  undecided: number;
  totalHT: string | null;
}
interface BrfLine {
  id: string;
  sku: string;
  name: string;
  quantity: number;
  unitPriceHT: string;
  expectedCreditHT: string | null;
  decision: 'ACCEPTED' | 'REFUSED' | null;
  decisionAmountHT: string | null;
  decisionNote: string | null;
  customerReturnId: string | null;
  customerReturnNumber: string | null;
}
const BRF_TABS = ['TO_SHIP', 'SHIPPED', 'CLOSED'] as const;
const BRF_STATUSES: Record<(typeof BRF_TABS)[number], string[]> = {
  TO_SHIP: ['CREATED', 'PREPARED'],
  SHIPPED: ['SHIPPED'],
  CLOSED: ['CREDIT_RECEIVED'],
};

export function ReturnsSuppliers() {
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const { can } = useReturnsRole();
  const [tab, setTab] = useState<(typeof BRF_TABS)[number]>('TO_SHIP');
  const [rows, setRows] = useState<BrfRow[] | null>(null);
  const [lines, setLines] = useState<Record<string, BrfLine[]>>({});
  const [answer, setAnswer] = useState<{ brf: BrfRow; line: BrfLine } | null>(null);
  const [policy, setPolicy] = useState<Policy | null>(null);
  useEffect(() => {
    apiClient
      .get<{ policy: Policy }>('/returns/policy')
      .then((r) => setPolicy(r.data.policy))
      .catch(() => undefined);
  }, []);

  const load = useCallback(() => {
    apiClient
      .get<BrfRow[]>('/supplier-returns')
      .then(async (r) => {
        setRows(r.data);
        const shown = r.data.filter((x) => BRF_STATUSES[tab].includes(x.status));
        const out: Record<string, BrfLine[]> = {};
        await Promise.all(
          shown.map(async (x) => {
            out[x.id] = (
              await apiClient.get<{ lines: BrfLine[] }>(`/supplier-returns/${x.id}`)
            ).data.lines;
          })
        );
        setLines(out);
      })
      .catch((e) => {
        setRows([]);
        showApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      push({ tone: 'success', message: msg });
      load();
    } catch (e) {
      showApiError(e);
    }
  };
  const days = (d: string | null) =>
    d ? Math.round((Date.now() - new Date(d).getTime()) / 864e5) : 0;
  const list = (rows ?? []).filter((x) => BRF_STATUSES[tab].includes(x.status));

  return (
    <div className="app-tokens">
      <PageHeader
        title={t('returnsV2.suppliers.title')}
        description={t('returnsV2.suppliers.description')}
      />
      <div className="mb-3 rounded-lg border bg-card shadow-sm">
        <div className="flex gap-1 overflow-x-auto px-3 pt-2" role="tablist">
          {BRF_TABS.map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              className={cn(
                '-mb-px whitespace-nowrap border-b-[3px] px-3 py-2 text-sm',
                tab === k
                  ? 'border-primary font-semibold'
                  : 'border-transparent text-muted-foreground'
              )}
            >
              {t(`returnsV2.suppliers.tab.${k}`)}
              <span className="ml-1.5 rounded-full bg-muted px-1.5 text-xs text-muted-foreground">
                {(rows ?? []).filter((x) => BRF_STATUSES[k].includes(x.status)).length}
              </span>
            </button>
          ))}
        </div>
      </div>
      {rows === null && (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          {t('returnsV2.loading')}
        </p>
      )}
      {rows && !list.length && (
        <Panel>
          <p className="px-4 py-12 text-center text-sm text-muted-foreground">
            {t('returnsV2.suppliers.empty')}
          </p>
        </Panel>
      )}
      {list.map((f) => {
        const late =
          f.status === 'SHIPPED' && days(f.shippedAt) > (policy?.supplierAnswerDays ?? 30);
        return (
          <Panel
            key={f.id}
            className="mb-3"
            title={
              <>
                <span className="font-mono">{f.brfNumber}</span> · {f.supplierName}
                {f.returnType ? ` · ${t(`returnsV2.brfType.${f.returnType}`)}` : ''}
              </>
            }
            actions={
              <div className="flex flex-wrap items-center gap-2">
                {late && (
                  <Badge variant="destructive">
                    {t('returnsV2.suppliers.silent', { days: days(f.shippedAt) })}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {t('returnsV2.suppliers.created', { date: formatDate(f.createdAt) })}
                  {f.shippedAt
                    ? ` · ${t('returnsV2.suppliers.shipped', { date: formatDate(f.shippedAt) })}`
                    : ''}
                </span>
                {f.status === 'CREATED' && can('ship') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      void act(
                        () => apiClient.post(`/supplier-returns/${f.id}/prepare`, {}, cmdHeaders()),
                        t('returnsV2.toast.prepared')
                      )
                    }
                  >
                    {t('returnsV2.suppliers.prepare')}
                  </Button>
                )}
                {f.status === 'PREPARED' && can('ship') && (
                  <Button
                    size="sm"
                    onClick={() =>
                      void act(
                        () => apiClient.post(`/supplier-returns/${f.id}/ship`, {}, cmdHeaders()),
                        t('returnsV2.toast.shipped')
                      )
                    }
                  >
                    {t('returnsV2.suppliers.ship')}
                  </Button>
                )}
              </div>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className={thCls}>{t('returnsV2.suppliers.col.part')}</th>
                    <th className={cn(thCls, 'text-right')}>{t('returnsV2.suppliers.col.qty')}</th>
                    <th className={thCls}>{t('returnsV2.suppliers.col.customerReturn')}</th>
                    <th className={cn(thCls, 'text-right')}>
                      {t('returnsV2.suppliers.col.expected')}
                    </th>
                    <th className={thCls}>{t('returnsV2.suppliers.col.answer')}</th>
                    <th className={thCls} />
                  </tr>
                </thead>
                <tbody>
                  {(lines[f.id] ?? []).map((l) => (
                    <tr key={l.id} className="border-b last:border-b-0">
                      <td className={tdCls}>
                        <b className="font-mono text-xs">{l.sku}</b>
                        <div className="text-xs text-muted-foreground">{l.name}</div>
                      </td>
                      <td className={cn(tdCls, 'text-right tabular-nums')}>{l.quantity}</td>
                      <td className={tdCls}>
                        {l.customerReturnId ? (
                          <Link
                            className="font-mono text-xs text-primary hover:underline"
                            to={`/returns/${l.customerReturnId}`}
                          >
                            {l.customerReturnNumber}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={cn(tdCls, 'text-right tabular-nums')}>
                        {formatEUR(
                          Number(l.expectedCreditHT ?? Number(l.unitPriceHT) * l.quantity)
                        )}
                      </td>
                      <td className={tdCls}>
                        {l.decision ? (
                          <>
                            <Badge variant={l.decision === 'ACCEPTED' ? 'success' : 'destructive'}>
                              {l.decision === 'ACCEPTED'
                                ? t('returnsV2.suppliers.credited', {
                                    amount: formatEUR(Number(l.decisionAmountHT)),
                                  })
                                : t('returnsV2.suppliers.refused')}
                            </Badge>
                            {l.decisionNote && (
                              <div className="text-xs text-muted-foreground">{l.decisionNote}</div>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={cn(tdCls, 'text-right')}>
                        {f.status === 'SHIPPED' && !l.decision && can('supplierAnswer') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setAnswer({ brf: f, line: l })}
                          >
                            {t('returnsV2.suppliers.enterAnswer')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        );
      })}
      {answer && (
        <AnswerDialog
          brf={answer.brf}
          line={answer.line}
          onClose={() => setAnswer(null)}
          onDone={() => {
            setAnswer(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function AnswerDialog({
  brf,
  line,
  onClose,
  onDone,
}: {
  brf: BrfRow;
  line: BrfLine;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { push } = useToast();
  const [accept, setAccept] = useState(true);
  const [amount, setAmount] = useState(
    String(
      Number(line.expectedCreditHT ?? Number(line.unitPriceHT) * line.quantity).toFixed(2)
    ).replace('.', ',')
  );
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const save = async () => {
    setErr('');
    try {
      await apiClient.post(
        `/supplier-returns/${brf.id}/lines/${line.id}/answer`,
        {
          accept,
          amountHT: accept ? Number(amount.replace(',', '.')) : null,
          note,
        },
        cmdHeaders()
      );
      push({ tone: 'success', message: t('returnsV2.toast.answered') });
      onDone();
    } catch (e) {
      setErr(returnError(t, e));
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-lg rounded-lg border bg-background shadow-xl"
      >
        <header className="border-b px-5 py-4">
          <h3 className="text-base font-semibold">
            {t('returnsV2.suppliers.answerTitle', { supplier: brf.supplierName })}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {brf.brfNumber} · {line.sku} × {line.quantity}
          </p>
        </header>
        <div className="grid gap-3 px-5 py-4">
          <div className="flex gap-2">
            {[true, false].map((a) => (
              <label
                key={String(a)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm',
                  accept === a && 'border-primary bg-primary-soft'
                )}
              >
                <input type="radio" checked={accept === a} onChange={() => setAccept(a)} />
                {a ? t('returnsV2.suppliers.accepted') : t('returnsV2.suppliers.refused')}
              </label>
            ))}
          </div>
          {accept && (
            <Field label={t('returnsV2.suppliers.amount')}>
              <Input
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Field>
          )}
          <Field label={t('returnsV2.suppliers.note')}>
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {line.customerReturnNumber && (
            <p className="text-xs text-muted-foreground">
              {t('returnsV2.suppliers.linked', { number: line.customerReturnNumber })}
            </p>
          )}
        </div>
        <footer className="flex flex-wrap items-center justify-end gap-2 border-t px-5 py-3">
          {err && (
            <p className="flex-1 text-sm font-medium text-destructive" role="alert">
              {err}
            </p>
          )}
          <Button variant="outline" onClick={onClose}>
            {t('returnsV2.cancel')}
          </Button>
          <Button onClick={() => void save()}>{t('returnsV2.save')}</Button>
        </footer>
      </div>
    </div>
  );
}

const POLICY_FIELDS: Array<keyof Policy> = [
  'returnDays',
  'feeIntactPct',
  'feeOpenedPct',
  'warrantyMonthsB2B',
  'coreDays',
  'rmaDays',
  'cashApprovalAbove',
  'supplierAnswerDays',
  'voucherMonths',
  'retractDays',
  'warrantyMonthsB2C',
  'cashLegalMax',
];

export function ReturnsRules() {
  const { t } = useTranslation();
  const { push } = useToast();
  const { can } = useReturnsRole();
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [legal, setLegal] = useState<string[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [err, setErr] = useState('');
  const own = can('rules');
  useEffect(() => {
    apiClient
      .get<{ policy: Policy; legal: string[] }>('/returns/policy')
      .then((r) => {
        setPolicy(r.data.policy);
        setLegal(r.data.legal);
        setDraft(Object.fromEntries(Object.entries(r.data.policy).map(([k, v]) => [k, String(v)])));
      })
      .catch(() => undefined);
  }, []);
  const save = async () => {
    setErr('');
    try {
      const body = Object.fromEntries(
        POLICY_FIELDS.filter((k) => !legal.includes(k)).map((k) => [
          k,
          Number(String(draft[k]).replace(',', '.')),
        ])
      );
      const { data } = await apiClient.put<{ policy: Policy }>(
        '/returns/policy',
        { policy: body },
        cmdHeaders()
      );
      setPolicy(data.policy);
      push({ tone: 'success', message: t('returnsV2.rules.saved') });
    } catch (e) {
      setErr(returnError(t, e));
    }
  };
  if (!policy)
    return (
      <p className="px-4 py-12 text-center text-sm text-muted-foreground">
        {t('returnsV2.loading')}
      </p>
    );
  return (
    <div className="app-tokens">
      <PageHeader
        title={t('returnsV2.rules.title')}
        description={t('returnsV2.rules.description')}
      />
      {!own && (
        <div className="mb-3 rounded-md bg-primary-soft px-3 py-2 text-sm text-primary">
          {t('returnsV2.rules.readOnly')}
        </div>
      )}
      <Panel>
        <table className="w-full text-sm">
          <tbody>
            {POLICY_FIELDS.map((k) => {
              const isLegal = legal.includes(k);
              return (
                <tr key={k} className="border-b last:border-b-0">
                  <td className={tdCls}>
                    <b>{t(`returnsV2.rules.f.${k}`)}</b>
                    <div className="text-xs text-muted-foreground">
                      {t(`returnsV2.rules.h.${k}`)}
                    </div>
                  </td>
                  <td className={cn(tdCls, 'w-40')}>
                    <Input
                      type="number"
                      value={draft[k] ?? ''}
                      disabled={!own || isLegal}
                      onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                    />
                  </td>
                  <td className={cn(tdCls, 'w-36')}>
                    {isLegal && <Badge variant="secondary">{t('returnsV2.rules.legal')}</Badge>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {own && (
          <div className="flex flex-wrap items-center gap-2 border-t px-4 py-3">
            {err && (
              <p className="flex-1 text-sm font-medium text-destructive" role="alert">
                {err}
              </p>
            )}
            <Button className="ml-auto" onClick={() => void save()}>
              {t('returnsV2.save')}
            </Button>
          </div>
        )}
      </Panel>
    </div>
  );
}

interface Ledger {
  creditNotes: Array<{
    id: string;
    number: string;
    createdAt: string;
    ttc: number;
    ht: number;
    vat: number;
    customerName: string | null;
    invoiceNumber: string | null;
    deliveryNoteNumber: string | null;
    returnId: string;
    returnNumber: string;
    imputed: number;
    refundChannels: string[] | null;
    voucherNumber: string | null;
  }>;
  pendingDeductions: Array<{
    id: string;
    returnId: string;
    returnNumber: string;
    customerName: string | null;
    sku: string;
    quantity: number;
    ttc: number;
    feeHt: number;
    deliveryNoteNumber: string | null;
    invoiceNumber: string | null;
  }>;
  vouchers: Array<{
    number: string;
    amount: number;
    balance: number;
    validUntil: string;
    expired: boolean;
    customerName: string | null;
    returnId: string | null;
    returnNumber: string | null;
  }>;
  exchanges: Array<{
    number: string;
    createdAt: string;
    returnId: string;
    returnNumber: string;
    customerName: string | null;
    lines: Array<{ sku: string; quantity: number }>;
  }>;
}
const MONEY_TABS = [
  'creditNotes',
  'pendingDeductions',
  'vouchers',
  'exchanges',
  'refunds',
] as const;

export function ReturnsMoney({ portal = false }: { portal?: boolean }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<(typeof MONEY_TABS)[number]>('creditNotes');
  const [l, setL] = useState<Ledger | null>(null);
  const [refunds, setRefunds] = useState<
    Array<{
      id: string;
      channel: string;
      amount: string | number;
      status: string;
      requestedAt?: string;
      returnNumber?: string | null;
      returnId?: string | null;
      customerName?: string | null;
    }>
  >([]);
  useEffect(() => {
    apiClient
      .get<Ledger>('/returns/ledger')
      .then((r) => setL(r.data))
      .catch(() => setL({ creditNotes: [], pendingDeductions: [], vouchers: [], exchanges: [] }));
    if (!portal)
      apiClient
        .get<{ data?: typeof refunds } | typeof refunds>(
          '/refunds?status=PENDING_APPROVAL,PENDING,FAILED,DONE,CANCELLED'
        )
        .then((r) => setRefunds(Array.isArray(r.data) ? r.data : (r.data.data ?? [])))
        .catch(() => setRefunds([]));
  }, [portal]);
  const tabs = MONEY_TABS.filter((k) => !(portal && (k === 'refunds' || k === 'exchanges')));
  const base = portal ? '/b2b/returns' : '/returns';
  const count = (k: (typeof MONEY_TABS)[number]) =>
    k === 'refunds' ? refunds.length : (l?.[k]?.length ?? 0);
  const retLink = (id: string | null | undefined, n: string | null | undefined) =>
    id ? (
      <Link className="font-mono text-xs text-primary hover:underline" to={`${base}/${id}`}>
        {n}
      </Link>
    ) : (
      '—'
    );
  const body = () => {
    if (!l)
      return (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          {t('returnsV2.loading')}
        </p>
      );
    const empty = (
      <tr>
        <td colSpan={9} className="px-4 py-10 text-center text-sm text-muted-foreground">
          {t('returnsV2.money.empty')}
        </td>
      </tr>
    );
    if (tab === 'creditNotes')
      return (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className={thCls}>{t('returnsV2.money.col.creditNote')}</th>
              {!portal && <th className={thCls}>{t('returnsV2.money.col.customer')}</th>}
              <th className={thCls}>{t('returnsV2.money.col.on')}</th>
              <th className={thCls}>{t('returnsV2.money.col.return')}</th>
              <th className={cn(thCls, 'text-right')}>{t('returnsV2.money.col.ttc')}</th>
              <th className={cn(thCls, 'text-right')}>{t('returnsV2.money.col.imputed')}</th>
              <th className={cn(thCls, 'text-right')}>{t('returnsV2.money.col.onAccount')}</th>
            </tr>
          </thead>
          <tbody>
            {l.creditNotes.length
              ? l.creditNotes.map((c) => (
                  <tr key={c.id} className="border-b last:border-b-0">
                    <td className={tdCls}>
                      <span className="font-mono text-xs">{c.number}</span>
                      <div className="text-xs text-muted-foreground">{formatDate(c.createdAt)}</div>
                    </td>
                    {!portal && <td className={tdCls}>{c.customerName}</td>}
                    <td className={cn(tdCls, 'font-mono text-xs')}>
                      {c.invoiceNumber ?? c.deliveryNoteNumber ?? '—'}
                    </td>
                    <td className={tdCls}>{retLink(c.returnId, c.returnNumber)}</td>
                    <td className={cn(tdCls, 'text-right font-semibold tabular-nums')}>
                      {formatEUR(c.ttc)}
                    </td>
                    <td className={cn(tdCls, 'text-right tabular-nums')}>{formatEUR(c.imputed)}</td>
                    <td className={cn(tdCls, 'text-right')}>
                      {c.voucherNumber
                        ? t('returnsV2.money.byVoucher', { number: c.voucherNumber })
                        : c.refundChannels?.length
                          ? c.refundChannels
                              .map((ch) => t(`refunds.channel.${ch}`, { defaultValue: ch }))
                              .join(', ')
                          : t('returnsV2.money.onAccountAmount', {
                              amount: formatEUR(Math.max(0, c.ttc - c.imputed)),
                            })}
                    </td>
                  </tr>
                ))
              : empty}
          </tbody>
        </table>
      );
    if (tab === 'pendingDeductions')
      return (
        <>
          <p className="px-4 pt-3 text-xs text-muted-foreground">
            {t('returnsV2.money.pendingHint')}
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                {!portal && <th className={thCls}>{t('returnsV2.money.col.customer')}</th>}
                <th className={thCls}>{t('returnsV2.money.col.return')}</th>
                <th className={thCls}>{t('returnsV2.money.col.line')}</th>
                <th className={thCls}>{t('returnsV2.money.col.invoice')}</th>
                <th className={cn(thCls, 'text-right')}>{t('returnsV2.money.col.ttc')}</th>
              </tr>
            </thead>
            <tbody>
              {l.pendingDeductions.length
                ? l.pendingDeductions.map((p) => (
                    <tr key={p.id} className="border-b last:border-b-0">
                      {!portal && <td className={tdCls}>{p.customerName}</td>}
                      <td className={tdCls}>{retLink(p.returnId, p.returnNumber)}</td>
                      <td className={tdCls}>
                        <span className="font-mono text-xs">{p.sku}</span> × {p.quantity}
                        {p.feeHt > 0 && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            {t('returnsV2.money.withFee')}
                          </span>
                        )}
                      </td>
                      <td className={cn(tdCls, 'text-xs')}>
                        {p.invoiceNumber ?? t('returnsV2.money.nextInvoice')}
                      </td>
                      <td className={cn(tdCls, 'text-right tabular-nums')}>−{formatEUR(p.ttc)}</td>
                    </tr>
                  ))
                : empty}
            </tbody>
          </table>
        </>
      );
    if (tab === 'vouchers')
      return (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className={thCls}>{t('returnsV2.money.col.voucher')}</th>
              {!portal && <th className={thCls}>{t('returnsV2.money.col.customer')}</th>}
              <th className={thCls}>{t('returnsV2.money.col.return')}</th>
              <th className={cn(thCls, 'text-right')}>{t('returnsV2.money.col.amount')}</th>
              <th className={cn(thCls, 'text-right')}>{t('returnsV2.money.col.balance')}</th>
              <th className={thCls}>{t('returnsV2.money.col.validUntil')}</th>
            </tr>
          </thead>
          <tbody>
            {l.vouchers.length
              ? l.vouchers.map((v) => (
                  <tr key={v.number} className="border-b last:border-b-0">
                    <td className={cn(tdCls, 'font-mono text-xs')}>{v.number}</td>
                    {!portal && <td className={tdCls}>{v.customerName}</td>}
                    <td className={tdCls}>{retLink(v.returnId, v.returnNumber)}</td>
                    <td className={cn(tdCls, 'text-right tabular-nums')}>{formatEUR(v.amount)}</td>
                    <td className={cn(tdCls, 'text-right tabular-nums')}>{formatEUR(v.balance)}</td>
                    <td className={tdCls}>
                      {formatDate(v.validUntil)}
                      {v.expired && (
                        <Badge variant="muted" className="ml-2">
                          {t('returnsV2.money.expired')}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))
              : empty}
          </tbody>
        </table>
      );
    if (tab === 'exchanges')
      return (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className={thCls}>{t('returnsV2.money.col.exchange')}</th>
              <th className={thCls}>{t('returnsV2.money.col.customer')}</th>
              <th className={thCls}>{t('returnsV2.money.col.return')}</th>
              <th className={thCls}>{t('returnsV2.money.col.parts')}</th>
            </tr>
          </thead>
          <tbody>
            {l.exchanges.length
              ? l.exchanges.map((x) => (
                  <tr key={x.number} className="border-b last:border-b-0">
                    <td className={tdCls}>
                      <span className="font-mono text-xs">{x.number}</span>
                      <div className="text-xs text-muted-foreground">{formatDate(x.createdAt)}</div>
                    </td>
                    <td className={tdCls}>{x.customerName}</td>
                    <td className={tdCls}>{retLink(x.returnId, x.returnNumber)}</td>
                    <td className={cn(tdCls, 'font-mono text-xs')}>
                      {x.lines.map((y) => `${y.sku} × ${y.quantity}`).join(', ')}
                    </td>
                  </tr>
                ))
              : empty}
          </tbody>
        </table>
      );
    return (
      <>
        <p className="px-4 pt-3 text-xs text-muted-foreground">
          {t('returnsV2.money.refundsHint')}{' '}
          <Link className="text-primary hover:underline" to="/refunds">
            {t('returnsV2.detail.refundsLink')}
          </Link>
        </p>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className={thCls}>{t('returnsV2.money.col.channel')}</th>
              <th className={thCls}>{t('returnsV2.money.col.customer')}</th>
              <th className={thCls}>{t('returnsV2.money.col.return')}</th>
              <th className={cn(thCls, 'text-right')}>{t('returnsV2.money.col.amount')}</th>
              <th className={thCls}>{t('returnsV2.money.col.state')}</th>
            </tr>
          </thead>
          <tbody>
            {refunds.length
              ? refunds.map((f) => (
                  <tr key={f.id} className="border-b last:border-b-0">
                    <td className={tdCls}>
                      {t(`refunds.channel.${f.channel}`, { defaultValue: f.channel })}
                    </td>
                    <td className={tdCls}>{f.customerName ?? '—'}</td>
                    <td className={tdCls}>{retLink(f.returnId, f.returnNumber)}</td>
                    <td className={cn(tdCls, 'text-right tabular-nums')}>
                      {formatEUR(Number(f.amount))}
                    </td>
                    <td className={tdCls}>
                      <Badge
                        variant={
                          f.status === 'DONE'
                            ? 'success'
                            : f.status === 'FAILED'
                              ? 'destructive'
                              : f.status === 'CANCELLED'
                                ? 'muted'
                                : 'warning'
                        }
                      >
                        {t(`refunds.status.${f.status}`, { defaultValue: f.status })}
                      </Badge>
                    </td>
                  </tr>
                ))
              : empty}
          </tbody>
        </table>
      </>
    );
  };
  return (
    <div className="app-tokens">
      {portal && <PortalReturnTabs />}
      <PageHeader
        title={portal ? t('returnsV2.money.portalTitle') : t('returnsV2.money.title')}
        description={
          portal ? t('returnsV2.money.portalDescription') : t('returnsV2.money.description')
        }
      />
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="flex gap-1 overflow-x-auto border-b px-3 pt-2" role="tablist">
          {tabs.map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              className={cn(
                '-mb-px whitespace-nowrap border-b-[3px] px-3 py-2 text-sm',
                tab === k
                  ? 'border-primary font-semibold'
                  : 'border-transparent text-muted-foreground'
              )}
            >
              {t(`returnsV2.money.tab.${k}`)}
              <span className="ml-1.5 rounded-full bg-muted px-1.5 text-xs text-muted-foreground">
                {count(k)}
              </span>
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">{body()}</div>
      </div>
    </div>
  );
}
